import { Injectable } from '@angular/core'
import { PlatformService } from '../platforms/platform.service'
import { LogService } from 'htv-lib'

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private worker?: Worker
  private pending = new Map<number, { resolve: (result: string) => void; reject: (err: Error) => void }>()
  private counter = 0

  constructor(
    private logService: LogService,
    private platformService: PlatformService
  ) {
    if (typeof Worker !== 'undefined') {
      this.worker = new Worker(new URL('../stringify.worker', import.meta.url))

      this.worker.onmessage = ({ data }) => {
        const resolve = this.pending.get(data.id)
        if (resolve) {
          this.pending.delete(data.id)
          if (data.error) {
            this.logService.error(`[StorageService] stringify worker error = ${data.error}`)
            resolve.reject(new Error(data.error))
          } else {
            resolve.resolve(data.result)
          }
        }
      }

      this.worker.onerror = (err: any) => {
        this.logService.error(`[StorageService] worker error = ${err?.message}`)
        this.pending.forEach(({ reject }) => reject(new Error(err?.message)))
        this.pending.clear()
        this.worker = undefined
      }
    } else {
      // Web Workers are not supported in this environment.
      // You should add a fallback so that your program still executes correctly.
    }
  }

  getString(key: string): string {
    return this.platformService.getValue(key)
  }

  getBoolean(key: string): boolean | undefined {
    const str = this.getString(key)
    if (str) {
      return str === 'true'
    } else {
      return undefined
    }
  }

  getDate(key: string): Date | undefined {
    try {
      const str = this.getString(key)
      if (str) {
        return new Date(str)
      } else {
        return undefined
      }
    } catch {
      return undefined
    }
  }

  getJson(key: string): any {
    try {
      const value = this.platformService.getValue(key)
      return JSON.parse(value)
    } catch (err: any) {
      this.logService.error(`Could not parse JSON string, error = ${err.message}`)
      return null
    }
  }

  saveString(key: string, value: string): void {
    this.platformService.setValue(key, value)
  }

  saveBoolean(key: string, value: boolean): void {
    this.saveString(key, value ? 'true' : 'false')
  }

  saveDate(key: string, value: Date): void {
    this.platformService.setValue(key, value)
  }

  async saveJson(key: string, value: any): Promise<void> {
    try {
      const str = await this.stringify(value)
      this.platformService.setValue(key, str)
    } catch (err: any) {
      this.logService.error(`[StorageService] Could not stringify, error = ${err?.message}`)
    }
  }

  stringify(obj: any): Promise<string> {
    if (this.worker) {
      const id = this.counter++
      return new Promise((resolve, reject) => {
        this.pending.set(id, { resolve, reject })
        if (this.worker) {
          this.worker.postMessage({ id, payload: obj })
        }
      })
    } else {
      return Promise.resolve(JSON.stringify(obj))
    }
  }

  removeValue(key: string): void {
    this.platformService.removeValue(key)
  }

  clearValues(): void {
    this.platformService.clearValues()
  }
}
